import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  webUrlFormSchema,
  type CreateWebUrlPayload,
  type UpdateWebUrlPayload,
  type WebUrl,
  type WebUrlFormValues,
} from '../types/webUrl.types';
import { useCreateWebUrl } from './useCreateWebUrl';
import { useUpdateWebUrl } from './useUpdateWebUrl';

interface UseWebUrlFormParams {
  webUrl: WebUrl | null;
  open: boolean;
  onClose: () => void;
}

const todayIsoDate = (): string => new Date().toLocaleDateString('en-CA');

const toIsoDate = (value: Date | null): string =>
  value === null ? '' : value.toLocaleDateString('en-CA');

const EMPTY_DEFAULTS: WebUrlFormValues = {
  title: '',
  description: '',
  actionLink: '',
  activeFrom: todayIsoDate(),
  activeTo: todayIsoDate(),
  isActive: true,
};

function toFormValues(webUrl: WebUrl | null): WebUrlFormValues {
  if (!webUrl) return EMPTY_DEFAULTS;
  return {
    title: webUrl.title ?? '',
    description: webUrl.description ?? '',
    actionLink: webUrl.actionLink ?? '',
    activeFrom: toIsoDate(webUrl.activeFrom),
    activeTo: toIsoDate(webUrl.activeTo),
    isActive: webUrl.isActive,
  };
}

function buildCreatePayload(values: WebUrlFormValues): CreateWebUrlPayload {
  const payload: CreateWebUrlPayload = {
    title: values.title,
    actionLink: values.actionLink,
    activeFrom: values.activeFrom,
    activeTo: values.activeTo,
    isActive: values.isActive,
  };
  if (values.description.trim().length > 0) {
    payload.description = values.description.trim();
  }
  return payload;
}

export function useWebUrlForm({ webUrl, open, onClose }: UseWebUrlFormParams) {
  const isEditMode = webUrl !== null;
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);

  const form = useForm<WebUrlFormValues>({
    resolver: zodResolver(webUrlFormSchema),
    defaultValues: toFormValues(webUrl),
    mode: 'onChange',
  });

  useEffect(() => {
    form.reset(open ? toFormValues(webUrl) : EMPTY_DEFAULTS);
  }, [webUrl, open, form]);

  const createMutation = useCreateWebUrl(onClose);
  const updateMutation = useUpdateWebUrl(onClose);

  const isPending = isEditMode ? updateMutation.isPending : createMutation.isPending;

  function submit(values: WebUrlFormValues) {
    if (isEditMode && webUrl) {
      const changedKeys = Object.keys(form.formState.dirtyFields) as (keyof WebUrlFormValues)[];
      if (changedKeys.length === 0) {
        onClose();
        return;
      }
      const payload: UpdateWebUrlPayload = Object.fromEntries(
        changedKeys.map((key) => [key, values[key]]),
      );
      updateMutation.mutate({ id: webUrl.id, payload });
      return;
    }
    createMutation.mutate(buildCreatePayload(values));
  }

  function requestClose() {
    if (!form.formState.isDirty) {
      onClose();
      return;
    }
    setIsDiscardDialogOpen(true);
  }

  return {
    form,
    isEditMode,
    isPending,
    submit,
    isDiscardDialogOpen,
    requestClose,
    confirmDiscard: () => {
      setIsDiscardDialogOpen(false);
      onClose();
    },
    cancelDiscard: () => setIsDiscardDialogOpen(false),
  };
}
